'use client'

import { Input } from '@headlessui/react'
import clsx from 'clsx'
import type { ControlledFormField, FormFieldCommonProps } from 'lib/types/Form'
import {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { Controller } from 'react-hook-form'
import InputAddon, { InputAddonProps } from '../InputAddon/InputAddon'
import InputError from '../InputError/InputError'
import InputHint from '../InputHint/InputHint'
import InputLabel from '../InputLabel/InputLabel'

interface CardNumberInputStandaloneProps
  extends InputAddonProps,
    Omit<FormFieldCommonProps, 'onChange'> {
  /**
   * The placeholder label for the input
   */
  placeholder?: string
  /**
   * Callback that runs when the input loses focus
   */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  /**
   * Callback that runs when the input value changes
   */
  onChange?: (data?: string | number, fieldName?: string) => void
}

export interface CardNumberInputProps
  extends ControlledFormField,
    CardNumberInputStandaloneProps {}

// Standalone component that can be used outside react-hook-form's context
export const CardNumberInputStandalone = forwardRef<
  HTMLInputElement,
  CardNumberInputStandaloneProps
>(function CardNumberInputStandaloneWithRef(
  {
    label,
    name,
    id = name,
    className,
    error,
    onBlur,
    onChange,
    defaultValue = '',
    disabled,
    hint,
    placeholder,
    ...props
  }: CardNumberInputStandaloneProps,
  ref,
) {
  const removeNonNumbers = (value: string | number) => {
    return String(value).replaceAll(/[^\d]+/g, '')
  }
  const formatValue = useCallback(
    (valueString: string | number = defaultValue) => {
      const noNonNumber = removeNonNumbers(valueString)
      const isAmex = /^3[47]/.test(noNonNumber)

      // If Amex, format shoud be ####-######-##### (4-6-5)
      if (isAmex) {
        const [four, six, five, rest] = (
          noNonNumber.match(/^(\d{0,4})(\d{0,6})(\d{0,5})(\d*)$/) || []
        )
          .slice(1)
          .filter(Boolean)

        return [four, six, five, ...(rest?.match(/(\d{4})|(\d+$)/g) || [])]
          .filter(Boolean)
          .join(' ')
      }

      // By default, group by digits of 4
      return noNonNumber.match(/(\d{4})|(\d+$)/g)?.join(' ') || ''
    },
    [defaultValue],
  )

  const [isEnabled, setIsEnabled] = useState(
    'conditionalEdit' in props && props.conditionalEdit ? false : !disabled,
  )
  const [currentValue, setCurrentValue] = useState(
    defaultValue ? defaultValue : '',
  )
  const [formattedValue, setFormattedValue] = useState(
    formatValue(currentValue),
  )
  const onInputChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      const noNonNumber = removeNonNumbers(value)
      const formattedValue = formatValue(noNonNumber)
      setCurrentValue(noNonNumber)
      setFormattedValue(formattedValue)
      onChange?.(noNonNumber, name)
    },
    [onChange, name, formatValue],
  )

  useEffect(() => {
    setFormattedValue(formatValue(currentValue))
  }, [currentValue, formatValue])

  useEffect(() => {
    setCurrentValue(defaultValue)
  }, [defaultValue])

  return (
    <div className={clsx(`text-input`, className)}>
      <div className="flex items-baseline justify-between">
        {label && <InputLabel htmlFor={id}>{label}</InputLabel>}
        <InputAddon
          {...props}
          {...('conditionalEdit' in props && props.conditionalEdit
            ? { onEdit: setIsEnabled }
            : {})}
        />
      </div>
      <Input
        type="number"
        name={name}
        value={currentValue}
        onChange={(...args) => {
          console.log({ args })
        }}
        readOnly
        hidden
      />
      <Input
        className={clsx(
          'w-full type-body-8 rounded-8 h-12 flex items-center px-4 my-2 transition-shadow lg:h-14',
          'disabled:bg-neutral-200 disabled:text-neutral-600 disabled:shadow-input-disabled disabled:cursor-not-allowed',
          'focus:shadow-input-focus focus:outline-none',
          'hover:shadow-input-active',
          error ? '!shadow-input-error' : 'shadow-input',
        )}
        id={id}
        placeholder={placeholder}
        onBlur={onBlur}
        onChange={onInputChange}
        disabled={!isEnabled}
        value={formattedValue}
        autoComplete="off"
        ref={ref}
        maxLength={19}
      />
      <InputHint>{hint}</InputHint>
      <InputError>{error}</InputError>
    </div>
  )
})

// Controlled form element that is automatically registered to react-hook-form
export default function CardNumberInput({
  name,
  control,
  rules,
  defaultValue,
  onBlur,
  onChange,
  disabled,
  ...props
}: CardNumberInputProps) {
  return (
    <Controller
      name={name || ''}
      defaultValue={defaultValue}
      control={control}
      rules={rules}
      render={({
        field: { onChange: fieldOnChange, ...field },
        fieldState,
      }) => {
        const onInputChange = (data?: number | string) => {
          fieldOnChange?.(data)
          onChange?.(data, name)
        }
        return (
          <CardNumberInputStandalone
            {...props}
            {...field}
            defaultValue={defaultValue}
            disabled={disabled}
            onBlur={onBlur}
            onChange={onInputChange}
            error={fieldState.error?.message}
          />
        )
      }}
    />
  )
}
